// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Text, Title } from '@mantine/core';
import { FC, useEffect, useRef } from 'react';
import { i18n } from '@lingui/core';
import { WorkflowDTO, WorkflowOperation, WorkflowStepDTO } from '../../../Types/LogT';
import StepExecutionStatusIcon from '../../../Atoms/workflows/StepExecutionStatusIcon';
import StepReport from './executionReport/StepReport';
import WorklowStepScheduleInfo from './WorkflowStepScheduleInfo';

interface Props {
  workflowStep: WorkflowStepDTO;
  currentStep: boolean;
  workflow: WorkflowDTO;
  reload: () => void;
  runOperation: (operation: WorkflowOperation) => void;
  previewOpened: boolean;
  disableLinks?: boolean;
}

const WorkflowStep: FC<Props> = ({
  workflowStep,
  currentStep,
  workflow,
  reload,
  runOperation,
  previewOpened,
  disableLinks = false,
}) => {
  const reloadAttemptsRef = useRef(0);
  const { startedAt, completedAt } = workflow;
  const { procedureType, executionReport, executionConfig } = workflowStep;
  const { executionStrategy, thresholdDate } = executionConfig;
  const { executionStatus } = executionReport ?? {};
  const scheduledImmediatelly = executionStrategy === 'AFTER_PREVIOUS_STEP' && !thresholdDate;

  useEffect(() => {
    let intervalId: NodeJS.Timer;
    reloadAttemptsRef.current = 0;

    if (startedAt && currentStep && scheduledImmediatelly && !executionStatus) {
      intervalId = setInterval(() => {
        if (reloadAttemptsRef.current >= 3) {
          clearInterval(intervalId);
          return;
        }
        reloadAttemptsRef.current += 1;
        reload();
      }, 2500);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionStrategy, startedAt, thresholdDate, executionStatus, currentStep]);

  return (
    <Box ml="xs" sx={{ opacity: (executionStatus && executionStatus !== 'SKIPPED') || currentStep ? 1 : 0.5 }}>
      <Title mb={2} order={6} td={executionStatus !== 'SKIPPED' ? 'none' : 'line-through'} weight="bolder">
        {i18n._(`actionType.${procedureType}`)}
      </Title>
      <StepReport disableLinks={disableLinks} workflowStep={workflowStep} previewOpened={previewOpened} />
      <Text mt={2} color="dimmed" size="xs">
        <WorklowStepScheduleInfo workflowStep={workflowStep} />
      </Text>
      {!completedAt && (
        <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
          <StepExecutionStatusIcon
            step={workflowStep}
            workflow={workflow}
            currentStep={currentStep}
            allowedOperations={workflow.possibleOperations}
            runOperation={runOperation}
          />
        </Box>
      )}
    </Box>
  );
};

export default WorkflowStep;
